import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaLock, FaUnlock } from 'react-icons/fa';
import { nudge, playSoftTone } from './openingUtils';

const SurpriseLockExperience = ({ modules, renderModules, openingMode, giftIntro = {} }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [gestureStep, setGestureStep] = useState(0);
  const gesture = ['Hold', 'Tap', 'Swipe'];
  const introTitle = giftIntro.title || 'Unlock the next surprise';
  const introCopy = giftIntro.subtitle || 'Complete the little unlock ritual before the next piece appears.';

  useEffect(() => {
    setActiveIndex(0);
    setGestureStep(0);
  }, [openingMode, modules?.length]);

  const activeModule = modules[activeIndex] || modules[0];
  const canUnlock = gestureStep >= gesture.length;

  const stopAdminLongPress = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const advanceGesture = (event) => {
    stopAdminLongPress(event);
    nudge(18);
    playSoftTone(500 + gestureStep * 80, 0.08);
    setGestureStep((value) => Math.min(gesture.length, value + 1));
  };

  const unlockNext = (event) => {
    stopAdminLongPress(event);
    if (!canUnlock) return;
    nudge([25, 35, 25]);
    playSoftTone(880, 0.14);
    setActiveIndex((value) => Math.min(modules.length - 1, value + 1));
    setGestureStep(0);
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-0 space-y-8">
      <div className="lumirae-opening-card relative mx-4 p-6 text-center">
        <div className="absolute inset-x-8 top-0 h-20 rounded-b-full blur-2xl" style={{ backgroundColor: 'var(--lumirae-accent-soft)' }} />
        <motion.div
          animate={{ scale: [1, 1.07, 1], rotate: activeIndex > 0 ? [0, -4, 4, 0] : 0 }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          className="relative mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-[1.5rem] shadow-xl"
          style={{ backgroundColor: 'var(--lumirae-surface-raised)', color: 'var(--lumirae-accent-primary)' }}
        >
          <FaLock className="text-3xl" />
        </motion.div>
        <div className="lumirae-opening-label">
          Surprise {activeIndex + 1} of {modules.length}
        </div>
        <h2 className="lumirae-opening-title mx-auto mt-3 max-w-xs text-2xl leading-tight">
          {introTitle}
        </h2>
        <p className="lumirae-opening-copy mx-auto mt-2 max-w-xs">
          {introCopy}
        </p>
        {activeIndex < modules.length - 1 && (
          <div
            className="mt-5 grid grid-cols-3 gap-2"
            onPointerDown={stopAdminLongPress}
            onPointerUp={stopAdminLongPress}
            onTouchStart={stopAdminLongPress}
            onTouchEnd={stopAdminLongPress}
          >
            {gesture.map((step, index) => (
              <motion.button
                key={step}
                onClick={advanceGesture}
                whileTap={{ scale: 0.94 }}
                disabled={gestureStep > index}
                className={`rounded-[1.25rem] border-2 px-2 py-3 text-[8px] font-black uppercase tracking-widest ${
                  gestureStep > index ? 'border-white text-white' : 'lumirae-opening-pill'
                }`}
                style={gestureStep > index ? { backgroundColor: 'var(--lumirae-accent-primary)' } : undefined}
              >
                {step}
              </motion.button>
            ))}
          </div>
        )}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={activeModule?.id || activeModule?._localId || activeIndex}
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -20 }}
          className="w-full overflow-visible"
        >
          {activeModule && renderModules([activeModule], 'full_page')}
        </motion.div>
      </AnimatePresence>
      {activeIndex < modules.length - 1 && (
        <div className="px-4">
          <motion.button
            onClick={unlockNext}
            onPointerDown={stopAdminLongPress}
            onPointerUp={stopAdminLongPress}
            whileTap={{ scale: 0.97 }}
            disabled={!canUnlock}
            className="lumirae-opening-button flex w-full items-center justify-center gap-3 py-6 text-[10px] font-black uppercase tracking-[0.3em] disabled:opacity-45"
          >
            <FaUnlock /> {canUnlock ? 'Unlock Next Surprise' : 'Complete the ritual'}
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default SurpriseLockExperience;
